
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { batch, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";

import { BrandliveEvent, FeatureFlagsEnum, LanguagesAbbr } from '../../../../../types/working-model';
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { switchingLanguageAlert } from './header/header';
import { blurEditor, loadWorkingEvent } from '../../../../../store/actions/admin/create-event';
import { ParamsProps } from '../../../../live-event/live-event';
import './event-editor-header.scss';
import useTranslationsV2 from "../../shared/use-translations-v2";
import EventTranslationsModal from "../../../header/event-translations-modal";
import useRerenderQuillOnLanguageChange from "../../../../../utils/use-rerender-quill-on-language-change";


interface IEventEditorHeader {
	workingEvent: BrandliveEvent | null;
}
const MAX_DEFAULT_LANGUAGES = 11;

const EventEditorHeader: React.FC<IEventEditorHeader> = ({ workingEvent }) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const displayEmail = useTypedSelector(state => state.CreateEventReducer.scheduledEmails?.displayEmail);
	const scheduledEmails = useTypedSelector(state => state.CreateEventReducer.scheduledEmails?.scheduledEmails);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const languagesContainerRef = useRef<HTMLDivElement | null>(null);

	const dispatch = useDispatch();
	const history = useHistory();
	const params = useParams<ParamsProps>();
	const useV2 = useTranslationsV2();
	const [maxDisplayedLanguages, setMaxDisplayedLanguages] = useState(MAX_DEFAULT_LANGUAGES);
	const [showExpandedBtn, setShowExpandedBtn] = useState(false);
	const [showEventTranslationsModal, setShowEventTranslationsModal] = useState(false);
	const { id, language: paramLanguage } = useParams() as ParamsProps;

	useRerenderQuillOnLanguageChange();

	// the email panel needs special handling to only show language(s) that the email is available in
	const isEmailPanel = history.location.pathname.includes('/emails/');
	const emailLanguages = useMemo(() => {
		if (isEmailPanel && displayEmail) {
			const fullScheduledEmail = scheduledEmails?.find(schedule => schedule.uuid === displayEmail);
			if (fullScheduledEmail?.language) {
				return [fullScheduledEmail.language];
			}
			if (fullScheduledEmail?.email_type.includes('session')) {
				const session = workingEvent?.sessions.find(session => session.uuid === fullScheduledEmail.session_uuid);
				if (session) {
					return session.languages as LanguagesAbbr[];
				}
			}
		}
	}, [displayEmail, isEmailPanel, scheduledEmails, workingEvent?.sessions]);

	// if no landing page, then single session w/o landing so pull langs from first session
	// otherwise, pull from homepage langs
	const languages = workingEvent?.homepage ?
		Array.from(new Set(workingEvent?.homepage?.languages as LanguagesAbbr[])) :
		Array.from(new Set(workingEvent?.sessions[0].languages as LanguagesAbbr[]));

	const redirect = useCallback((language: LanguagesAbbr) => {
		const endsWithLang = history.location.pathname.endsWith(`/${paramLanguage}`);
		history.replace(history.location.pathname.replace(
			endsWithLang ? `/${paramLanguage}` : `/${paramLanguage}/`,
			endsWithLang ? `/${language}` : `/${language}/`
		));
	}, [history, paramLanguage]);

	// if the language param in the browser is not one of the available email languages, redirect to another language
	useEffect(() => {
		if (!!emailLanguages && !emailLanguages.includes(paramLanguage)) {
			redirect(emailLanguages[0]);
		}
	}, [emailLanguages, paramLanguage, redirect]);

	const handleLanguageClick = (language: LanguagesAbbr) => async () => {
		if (!token) return;
		if (!useV2) {
			switchingLanguageAlert();
			batch(() => {
				dispatch(blurEditor(true));
				dispatch(loadWorkingEvent(id, token));
			});
		}
		redirect(language);
	};

	const handleExpandLanguage = () => {
		setMaxDisplayedLanguages(languages.length);
		setShowExpandedBtn(!showExpandedBtn);
	};

	const handleMinimizeExpandLanguage = () => {
		setMaxDisplayedLanguages(MAX_DEFAULT_LANGUAGES);
		setShowExpandedBtn(!showExpandedBtn);
	};

	const allLanguages = useMemo(() => {
		if (!workingEvent) {
			return [];
		} else {
			const set = new Set(workingEvent.sessions.flatMap(session => session.languages));
			return Array.from(set).sort();
		}
	}, [workingEvent]);

	return (
		<div className='event-editor-header'>
			<div className='event-editor-header-column event-editor-header-languages'>
				<h6 className="event-editor-header-titles">Languages</h6>
				<div ref={languagesContainerRef} className="event-editor-langauges-container">
					{featureFlags[FeatureFlagsEnum.translations_v2] && (
						<div className="event-editor-language-item" style={{ marginRight: 8 }}>
							<button
								className={workingEvent?.settings.translation_version === 2 ? 'on' : ''}
								onClick={() => setShowEventTranslationsModal(true)}
							>
								Translations
							</button>
						</div>
					)}

					{languages.map((language: LanguagesAbbr, index: number) => {
						if (index < maxDisplayedLanguages) {
							return (
								<div key={language} className="event-editor-langauge-item">
									<button
										className={params?.language === language ? 'active' : ''}
										onClick={handleLanguageClick(language)}
									>
										{language}
									</button>
								</div>
							);
						}
					})}

					{languages.length > maxDisplayedLanguages ? (
						< div className="event-editor-langauge-item">
							<button onClick={handleExpandLanguage}>
								+{languages.length - maxDisplayedLanguages}
							</button>
						</div>
					) :
						(showExpandedBtn) && (
							<div className="event-editor-langauge-item">
								<button onClick={handleMinimizeExpandLanguage}>
									-
								</button>
							</div>
						)
					}
				</div>
			</div>

			{featureFlags[FeatureFlagsEnum.translations_v2] && (
				<EventTranslationsModal
					open={showEventTranslationsModal}
					closeFn={() => setShowEventTranslationsModal(false)}
					languages={allLanguages}
				/>
			)}
		</div>
	);
};

export default EventEditorHeader;